// Asking the legacy .NET parent to navigate ITS OWN top-level window.
//
// We are an iframe inside the legacy portal. A hand-off to a legacy .aspx page can go
// two ways:
//   • window.open(url, '_top')  — we navigate the top-level window ourselves. Works with
//     no parent cooperation as long as the frame is allowed to navigate its top.
//   • ask the parent to navigate itself — same result, but the parent stays in charge.
// Either way it is ONE tab: the legacy page replaces the portal page that framed us.
//
// The decision is made SYNCHRONOUSLY, from a capability the parent declares during the
// auth handshake. We deliberately do NOT post-then-wait-for-an-ack: any await between
// the click and window.open puts the fallback outside the user-gesture stack. Parent says
// nothing → we take `_top` ourselves.
//
// Parent contract (an addition to EMBEDDED_SSO.md §5):
//   parent → iframe  pcs:auth:token  { handoffToken, sessionParameter?, capabilities? }
//       `capabilities` may include 'navigate' — "I listen for pcs:navigate".
//   iframe → parent  pcs:navigate    { url }
//       the parent sets window.top.location to `url`.
import { PARENT_ORIGINS } from './embeddedConfig';

let parentCapabilities = [];

/** Record what the parent said it can do (from the handshake reply). */
export function setParentCapabilities(list) {
  parentCapabilities = Array.isArray(list) ? list : [];
}

export function clearParentCapabilities() {
  parentCapabilities = [];
}

/** True only when we are really framed AND the parent declared 'navigate'. */
export function parentCanNavigate() {
  return isFramed() && parentCapabilities.includes('navigate');
}

/** Are we inside a frame at all? (Cross-origin access to window.parent can throw.) */
export function isFramed() {
  try {
    return window.parent && window.parent !== window;
  } catch {
    return true; // access denied means there IS a cross-origin parent
  }
}

/**
 * Ask the parent to take its top-level window to `url`.
 * @returns {boolean} true if the message was posted (caller then skips window.open).
 */
export function navigateParent(url) {
  if (!parentCanNavigate()) return false;
  // Post to EVERY allowed origin, same as the auth handshake: the browser delivers a
  // message only to a window whose origin matches the targetOrigin, so at most the real
  // parent ever sees it. A single origin would be wrong here — one built image is framed
  // by the dev, uat and prod Relius hosts.
  let posted = false;
  PARENT_ORIGINS.forEach((origin) => {
    try {
      window.parent.postMessage({ type: 'pcs:navigate', url }, origin);
      posted = true;
    } catch {
      // parent gone / origin mismatch — try the remaining origins
    }
  });
  return posted; // false → caller falls back to a new tab
}

export default navigateParent;
